import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLang } from "@/context/LanguageContext";
import { galleryImages } from "@/lib/gallery";
import { Loader2, X } from "lucide-react";
import { shuffle } from "lodash";

const PAGE_SIZE = 6;

export default function Gallery() {
  const { t } = useLang();
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const sentinelRef = useRef(null);

  const loadMore = useCallback(() => {
    if (loading) return;
    if (visible >= galleryImages.length) return;
    setLoading(true);
    // small delay simulates smooth async load — feels intentional
    setTimeout(() => {
      setVisible((v) => Math.min(v + PAGE_SIZE, galleryImages.length));
      setLoading(false);
    }, 350);
  }, [visible, loading]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "400px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  // Lightbox keyboard nav
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % visible);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + visible) % visible);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, visible]);

  const items =shuffle(galleryImages.slice(0, visible));
  const done = visible >= galleryImages.length;

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-12">
          <div className="font-overline text-terracotta text-xs mb-3">{t.gallery.overline}</div>
          <h2 className="font-display text-4xl md:text-6xl text-jungle leading-[1.05]">{t.gallery.title}</h2>
        </div>

        <div
          data-testid="gallery-grid"
          className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4 space-y-3 md:space-y-4"
        >
          {
          items.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(i)}
              data-testid={`gallery-img-${i}`}
              className="block w-full break-inside-avoid overflow-hidden group relative fade-up"
              style={{ animationDelay: `${(i % PAGE_SIZE) * 40}ms` }}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-jungle/0 group-hover:bg-jungle/25 transition-colors" />
            </button>
          ))}
        </div>

        <div ref={sentinelRef} className="h-1" data-testid="gallery-sentinel" />

        <div className="mt-10 flex justify-center" data-testid="gallery-status">
          {loading && (
            <div className="flex items-center gap-2 text-forest font-overline text-xs">
              <Loader2 size={16} className="animate-spin text-terracotta" /> Loading…
            </div>
          )}
          {!loading && !done && (
            <button onClick={loadMore} data-testid="gallery-load-more" className="btn-ghost">
              Load more
            </button>
          )}
          {done && (
            <div className="font-overline text-[10px] text-forest">— End of gallery —</div>
          )}
        </div>
      </div>

      {lightbox !== null && items[lightbox] && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-jungle/95 flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-6 right-6 text-sand/80 hover:text-terracotta"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={28} />
          </button>
          <img
            src={items[lightbox].src}
            alt={items[lightbox].alt}
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-6 text-sand/60 font-overline text-xs">
            {lightbox + 1} / {items.length}
          </div>
        </div>
      )}
    </section>
  );
}
